import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./FeedCard.module.css";
import ApiButton from "../ApiButton/ApiButton";
import { getImagemComunicadoUrl } from "../../services/api";

// Aceita tanto "dd/mm/yyyy hh:mm:ss" (formato que datger/datcom sempre
// deveriam vir) quanto "yyyy-mm-dd hh:mm:ss"/ISO (caso o backend mande cru) -
// sem isso, uma data no formato errado caía direto no fallback e mostrava a
// string crua sem formatar nenhuma.
function parseDataFlexivel(data) {
    const br = data.match(/^(\d{2})\/(\d{2})\/(\d{4})[ T]?(\d{2}:\d{2}(:\d{2})?)?/);
    if (br) {
        const [, d, m, y, hms] = br;
        const dt = new Date(`${y}-${m}-${d}T${hms || '00:00:00'}`);
        return isNaN(dt) ? null : dt;
    }
    const dt = new Date(data.replace(' ', 'T'));
    return isNaN(dt) ? null : dt;
}

function tempoRelativo(data) {
    if (!data) return '';
    const date = parseDataFlexivel(data);
    if (!date) return data;
    const diff = Date.now() - date.getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins  <  2) return 'agora';
    if (mins  < 60) return `há ${mins}min`;
    if (hours < 24) return `há ${hours}h`;
    if (days  ===1) return 'ontem';
    if (days  <  7) return `há ${days} dias`;
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
}

// Data exibida no feed: se o comunicado foi AGENDADO (tipcom === 'agendado'),
// mostra a data/hora do agendamento (datcom+horcom), não a de criação -
// pedido do usuário, 2026-10-20: "pode ser que fizemos um agendamento dele,
// e seria a data do agendamento". Publicação "agora" (ou sem datcom
// cadastrado) continua usando datger (data de criação/GETDATE do insert).
function dataExibidaFeed(post) {
    if (post.tipcom === 'agendado' && post.datcom) {
        return `${post.datcom} ${post.horcom || '00:00'}`;
    }
    return post.datger;
}

export default function FeedCard({ post, onLike, onLove, onView, onComment }) {
    const navigate = useNavigate();
    const [commentOpen, setCommentOpen] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [imgVisible, setImgVisible] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        if (!post.urlimg) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setImgVisible(true); observer.disconnect(); } },
            { rootMargin: "200px" }
        );
        if (cardRef.current) observer.observe(cardRef.current);
        return () => observer.disconnect();
    }, [post.urlimg]);

    const handleOpen = () => {
        onView(post.codcom);
        navigate(`/comunicado/${post.codcom}`, { state: { post } });
    };

    const handleLike = (e) => { e.stopPropagation(); onLike(post.codcom); };
    const handleLove = (e) => { e.stopPropagation(); onLove(post.codcom); };
    const handleCommentClick = (e) => { e.stopPropagation(); setCommentOpen(v => !v); };

    const totalReacoes = (post.curtidas ?? 0) + (post.ameis ?? 0);

    return (
        <div
            ref={cardRef}
            className={`${styles.card} ${post.viscom > 0 ? styles.viewed : ""}`}
            onClick={handleOpen}
        >
            {/* HEADER */}
            <div className={styles.postHeader}>
                <div className={styles.avatar}>{post.setcom?.charAt(0)}</div>
                <div className={styles.postInfo}>
                    <strong>{post.setcom}</strong>
                    <span>{tempoRelativo(dataExibidaFeed(post))}</span>
                </div>
                {!post.viscom && <span className={styles.newBadge}>NOVO</span>}
            </div>

            {/* CONTEÚDO */}
            <h4 className={styles.title} dangerouslySetInnerHTML={{ __html: post.titcom }} />
            {post.subcom && <p className={styles.text} dangerouslySetInnerHTML={{ __html: post.subcom }} />}

            {/* IMAGEM */}
            {post.urlimg && imgVisible && (
                <div className={styles.cardImageWrap}>
                    <img
                        src={getImagemComunicadoUrl(post.codcom)}
                        className={styles.cardImage}
                        decoding="async"
                        alt=""
                    />
                </div>
            )}

            {/* BARRA DE REAÇÕES estilo Facebook */}
            {(totalReacoes > 0 || post.mensagens > 0) && (
                <div className={styles.reactionBar}>
                    <span className={styles.reactionLeft}>
                        {post.curtidas > 0 && '👍'}
                        {post.ameis > 0 && '❤️'}
                        {totalReacoes > 0 && <span>{totalReacoes}</span>}
                    </span>
                    <span className={styles.reactionRight}>
                        {post.mensagens > 0
                            ? `${post.mensagens} comentário${post.mensagens !== 1 ? 's' : ''}`
                            : post.mensagens === undefined ? '—' : ''
                        }
                    </span>
                </div>
            )}

            {/* BOTÕES */}
            <div className={styles.cardFooter} onClick={e => e.stopPropagation()}>
                <ApiButton
                    className={`${styles.actionButton} ${post.curcom > 0 ? styles.activeLike : ''}`}
                    onClick={handleLike}
                >
                    👍 Curtir
                </ApiButton>
                <ApiButton
                    className={`${styles.actionButton} ${post.amecom > 0 ? styles.activeLove : ''}`}
                    onClick={handleLove}
                >
                    ❤️ Amei
                </ApiButton>
                {post.bloqcom ? (
                    <button className={`${styles.actionButton} ${styles.actionButtonBloqueado}`} disabled>
                        🔒 Comentar
                    </button>
                ) : (
                    <button className={styles.actionButton} onClick={handleCommentClick}>
                        💬 Comentar
                    </button>
                )}
            </div>

            {/* INPUT COMENTÁRIO */}
            {commentOpen && !post.bloqcom && (
                <div className={styles.commentSection} onClick={e => e.stopPropagation()}>
                    <div className={styles.commentInput}>
                        <input
                            type="text"
                            placeholder="Escreva um comentário..."
                            value={newComment}
                            onChange={e => setNewComment(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && newComment.trim()) {
                                    onComment(post.codcom, newComment.trim());
                                    setNewComment('');
                                    setCommentOpen(false);
                                }
                            }}
                        />
                        <ApiButton
                            onClick={() => {
                                if (!newComment.trim()) return;
                                onComment(post.codcom, newComment.trim());
                                setNewComment('');
                                setCommentOpen(false);
                            }}
                        >
                            Enviar
                        </ApiButton>
                    </div>
                </div>
            )}
        </div>
    );
}
